import { BASIS_POINTS_DIVISOR } from '@ui/constants/uiConfig';
import { Text } from '@ui/lib/park/ui/text';
import { calculatePositionTotalPoints } from '@ui/services/contract/hooks/GenesisAirdrop/points';
import { PositionBasicPoints } from '@ui/services/contract/hooks/GenesisAirdrop/useBatchPositionBasicPoints';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { formatDurationShort } from '@ui/utils/formatDurationShort';
import { formatMultiplier } from '@ui/utils/formatMultiplier';
import { intervalToDuration } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Box, HStack, Stack } from 'styled-system/jsx';

import { SizeUsd } from '../SizeUsd';
import { valueStyle } from './styles';

export const PointsTooltipContent = ({
  position,
  points,
}: {
  position: Position;
  points?: PositionBasicPoints;
}) => {
  const { t } = useTranslation();

  const hasBasicPoints =
    !!points && Number(points.openPoints) + Number(points.closePoints) > 0;

  const basePoints = points
    ? Number(points.openPoints) + Number(points.closePoints)
    : undefined;
  const holdingMultiplier = points
    ? Number(points.holdingBoostP) / BASIS_POINTS_DIVISOR
    : undefined;
  const teamMultiplier = points
    ? Number(points.teamBoostP) / BASIS_POINTS_DIVISOR
    : undefined;

  // Treat the first 3 minutes (normal) or 1 minute (degen) after opening as a countdown window
  const now = new Date();
  const positionOpenMs = position.timestamp * 1000;
  const countdownDurationMs = (position.pair.isDegenPair ? 1 : 3) * 60 * 1000;
  const countdownEndMs = positionOpenMs + countdownDurationMs;
  const isInCountdown = now.getTime() < countdownEndMs;

  return (
    <Stack minW="300px" gap={4}>
      <Box
        borderBottom="1px solid"
        borderBottomColor="greyWhite.outline"
        pb={2}
        textStyle="headings.small"
        color="greyWhite.white"
      >
        <Text textAlign="left">{t('common.Position Points (Est.)')}</Text>
      </Box>

      <Stack gap={3}>
        <HStack justify="space-between">
          <HStack justify="space-between">
            <Text
              textStyle="text.small"
              color="#A6A6A6"
              maxW="52"
              textAlign="left"
            >
              a) {t('common.Position Size')}:
            </Text>
            <Text css={valueStyle} maxW="52" textAlign="left">
              <SizeUsd genericTradeParams={position} css={valueStyle} />
            </Text>
          </HStack>
          <Text
            css={{
              ...valueStyle,
              color:
                basePoints !== undefined && basePoints > 0
                  ? 'price.priceUp'
                  : undefined,
            }}
          >
            {basePoints !== undefined ? basePoints / 100 : '—'}
          </Text>
        </HStack>

        <HStack justify="space-between">
          <HStack justify="space-between">
            <Text
              textStyle="text.small"
              color="#A6A6A6"
              maxW="52"
              textAlign="left"
            >
              b) {t('common.Holding Duration')}:
            </Text>

            {isInCountdown ? (
              <Text
                css={{
                  ...valueStyle,
                  color: 'price.priceDown',
                }}
              >
                {'-'}
                {formatDurationShort(
                  intervalToDuration({
                    start: now,
                    end: new Date(countdownEndMs),
                  }),
                )}
              </Text>
            ) : (
              <Text css={valueStyle}>
                {formatDurationShort(
                  intervalToDuration({
                    start: position.timestamp * 1000,
                    end: now,
                  }),
                )}
              </Text>
            )}
          </HStack>
          <Text
            css={{
              ...valueStyle,
              color: isInCountdown
                ? 'price.priceDown'
                : holdingMultiplier !== undefined && holdingMultiplier > 1
                ? 'price.priceUp'
                : undefined,
            }}
          >
            {'x'}
            {formatMultiplier(isInCountdown ? 0 : holdingMultiplier)}
          </Text>
        </HStack>

        <HStack justify="space-between">
          <Text
            textStyle="text.small"
            color="#A6A6A6"
            maxW="52"
            textAlign="left"
          >
            c) {t('common.Team Boost')}
          </Text>
          <Text
            css={{
              ...valueStyle,
              color:
                teamMultiplier !== undefined && teamMultiplier > 1
                  ? 'price.priceUp'
                  : undefined,
            }}
          >
            {'x'}
            {formatMultiplier(teamMultiplier)}
          </Text>
        </HStack>
      </Stack>

      <Box
        borderTop="1px solid"
        borderTopColor="greyWhite.outline"
        pt={3}
        display="flex"
        justifyContent="space-between"
      >
        <Text textStyle="headings.small" color="greyWhite.white">
          {t('common.Total')}
        </Text>
        {hasBasicPoints ? (
          <Text
            css={{
              ...valueStyle,
              textStyle: 'headings.small',
              color: isInCountdown ? undefined : 'price.priceUp',
            }}
          >
            {(isInCountdown
              ? 0
              : calculatePositionTotalPoints(points!)
            ).toLocaleString()}
          </Text>
        ) : (
          <Text
            css={{
              ...valueStyle,
              textStyle: 'headings.small',
            }}
          >
            —
          </Text>
        )}
      </Box>
    </Stack>
  );
};
